import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../config/axios.config';
import PlanCard from '../../components/subscription/PlanCard';

/**
 * Plan Selection Page
 * Displays all available subscription plans for tenant to choose from
 */
const PlanSelection = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);

    useEffect(() => {
        fetchPlansAndSubscription();
    }, []);

    const fetchPlansAndSubscription = async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch available plans
            const plansResponse = await axiosInstance.get('/subscription/plans');

            if (plansResponse.data.success) {
                setPlans(plansResponse.data.data);
            }

            // Fetch current subscription
            try {
                const subResponse = await axiosInstance.get('/subscription/current');
                if (subResponse.data.success && subResponse.data.data) {
                    setCurrentSubscription(subResponse.data.data);
                }
            } catch (subError) {
                console.warn('No current subscription:', subError);
            }

        } catch (err) {
            console.error('Error fetching plans:', err);
            setError(err.response?.data?.message || 'Failed to load subscription plans');
        } finally {
            setLoading(false);
        }
    };

    const handlePlanSelect = async (plan) => {
        setSelectedPlan(plan);

        // Get current plan price
        const currentPlanPrice = currentSubscription?.plan?.price_monthly || 0;
        const newPlanPrice = plan.price_monthly || 0;

        // Check if it's a downgrade (going to cheaper/same price plan) or free plan
        const isDowngradeOrFree = newPlanPrice <= currentPlanPrice;
        const isUpgrade = newPlanPrice > currentPlanPrice;

        // TEMPORARY: Allow all plan changes without payment for testing
        // TODO: Remove this and implement Razorpay payment for upgrades

        if (isUpgrade) {
            // Show confirmation for upgrades
            const confirmUpgrade = window.confirm(
                `⚠️ TESTING MODE - NO PAYMENT REQUIRED\n\n` +
                `You are about to upgrade to ${plan.name} (₹${plan.price_monthly}/month).\n\n` +
                `Current Plan: ${currentSubscription?.plan?.name || 'None'} (₹${currentPlanPrice}/month)\n` +
                `New Plan: ${plan.name} (₹${newPlanPrice}/month)\n\n` +
                `In production, this would require payment via Razorpay.\n\n` +
                `Do you want to proceed with the upgrade (for testing)?`
            );

            if (!confirmUpgrade) {
                setSelectedPlan(null);
                return;
            }
        }

        // Change plan immediately (works for both upgrades and downgrades in testing mode)
        try {
            const response = await axiosInstance.post('/subscription/change-plan', {
                plan_id: plan.id,
                billing_interval: 'monthly',
                skip_payment: true  // Temporary flag for testing
            });

            if (response.data.success) {
                const actionType = isUpgrade ? 'upgraded' : isDowngradeOrFree ? 'downgraded' : 'changed';
                alert(`✅ Successfully ${actionType} to ${plan.name}!\n\nYour menu and features will update automatically.`);
                // Navigate back to subscription dashboard
                navigate('/app/subscription');
                // Reload to refresh menu
                setTimeout(() => window.location.reload(), 500);
            } else {
                alert(`Error: ${response.data.message}`);
                setSelectedPlan(null);
            }
        } catch (error) {
            console.error('Error changing plan:', error);
            console.error('Error response:', error.response);
            console.error('Error data:', error.response?.data);

            // Get detailed error message
            let errorMessage = 'Unknown error';
            if (error.response?.data?.debug) {
                errorMessage = `${error.response.data.debug.error} (${error.response.data.debug.file}:${error.response.data.debug.line})`;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            alert(`Failed to change plan: ${errorMessage}`);
            setSelectedPlan(null);
        }
    };

    if (loading) {
        return (
            <div className="grid">
                <div className="col-12">
                    <h2>Subscription Plans</h2>
                </div>
                {[1, 2, 3].map((i) => (
                    <div key={i} className="col-12 md:col-6 lg:col-4">
                        <Card>
                            <Skeleton height="3rem" className="mb-3" />
                            <Skeleton height="5rem" className="mb-3" />
                            <Skeleton height="10rem" />
                        </Card>
                    </div>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Card>
                        <div className="text-center p-4">
                            <i className="pi pi-exclamation-triangle text-red-500" style={{ fontSize: '3rem' }}></i>
                            <h3 className="mt-3">Error Loading Plans</h3>
                            <p className="text-600">{error}</p>
                            <Button label="Retry" icon="pi pi-refresh" onClick={fetchPlansAndSubscription} className="mt-3" />
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    const currentPlanId = currentSubscription?.plan?.id;

    return (
        <div className="grid">
            {/* Page Header */}
            <div className="col-12">
                <div className="flex justify-content-between align-items-center mb-3">
                    <div>
                        <h2 className="m-0">Choose Your Plan</h2>
                        <p className="text-600 mt-2 mb-0">Select the plan that best fits your needs</p>
                    </div>
                    <Button
                        label="Back to Dashboard"
                        icon="pi pi-arrow-left"
                        className="p-button-text"
                        onClick={() => navigate('/app/subscription')}
                    />
                </div>
            </div>

            {/* Current Plan Info Banner */}
            {currentSubscription && (
                <div className="col-12">
                    <Card className="bg-blue-50 border-blue-200">
                        <div className="flex align-items-center">
                            <i className="pi pi-info-circle text-blue-500 mr-3" style={{ fontSize: '2rem' }}></i>
                            <div className="flex-1">
                                <div className="font-semibold text-blue-900">
                                    You are currently on the <strong>{currentSubscription.plan.name}</strong> plan
                                </div>
                                <div className="text-sm text-blue-700 mt-1">
                                    {currentSubscription.plan.feature_count || currentSubscription.plan.features?.length || 0} features included
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {/* Plans Grid */}
            {plans.length > 0 ? (
                plans.map((plan) => (
                    <div key={plan.id} className="col-12 md:col-6 lg:col-4">
                        <PlanCard
                            plan={plan}
                            isCurrentPlan={plan.id === currentPlanId}
                            onSelect={handlePlanSelect}
                            loading={selectedPlan?.id === plan.id}
                        />
                    </div>
                ))
            ) : (
                <div className="col-12">
                    <Card>
                        <div className="text-center p-4">
                            <i className="pi pi-inbox text-600" style={{ fontSize: '3rem' }}></i>
                            <h3 className="mt-3">No Plans Available</h3>
                            <p className="text-600">No subscription plans are currently available.</p>
                        </div>
                    </Card>
                </div>
            )}

            {/* FAQ Section */}
            <div className="col-12 mt-4">
                <Card title="Frequently Asked Questions">
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <h4 className="mt-0">Can I change my plan later?</h4>
                            <p className="text-600">Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
                        </div>
                        <div className="col-12 md:col-6">
                            <h4 className="mt-0">What payment methods do you accept?</h4>
                            <p className="text-600">We accept all major credit/debit cards, UPI, and net banking through Razorpay.</p>
                        </div>
                        <div className="col-12 md:col-6">
                            <h4 className="mt-0">Are credits rolled over?</h4>
                            <p className="text-600">Unused credits expire at the end of each billing cycle and do not roll over.</p>
                        </div>
                        <div className="col-12 md:col-6">
                            <h4 className="mt-0">Can I cancel anytime?</h4>
                            <p className="text-600">Yes, you can cancel your subscription at any time. You'll retain access until the end of your billing period.</p>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default PlanSelection;
